import { Injectable } from '@angular/core';
import { ResourceItemModel } from 'src/app/models/resource';
import { BoardCloneService } from 'src/app/services/board-clone.service';
import { UploadFile } from 'src/app/services/document-upload.service';
import { CenterPosition } from 'src/app/services/items-canvas.service';
import { SessionSharedDataService } from 'src/app/services/session-shared-data.service';
import { SpaceRepository } from 'src/app/state/space.repository';
import { SpaceBoardsService } from 'src/app/services/space-boards.service';
import { PDFBoardStrategy } from 'src/app/models/upload';
import {
  BoardPdfFactoryService,
  PdfArrangement,
} from 'src/app/sessions/session/pdf-canvas-item/pdf-services/board-pdf-factory.service';
import { isImageFile } from 'src/app/services/image-file-type.utils';
import { BulkUploadFile, BulkUploadFileStatus } from './bulk-upload.service';

const ACCEPTED_LIBREOFFICE_MIME_TYPES = [
  'application/msword',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.oasis.opendocument.presentation',
  'application/vnd.oasis.opendocument.text',
  'application/x-hwp',
  'application/x-hwpx',
];

const ACCEPTED_LIBREOFFICE_EXTENSIONS = ['.hwp', '.hwpx'];

export interface BulkUploadSubmitOptions {
  files: BulkUploadFile[];
  strategy: PDFBoardStrategy;
  arrangement?: PdfArrangement;
  uploadAsSeparateImages?: boolean;
  renderInNearestFreeSpace?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class BulkUploadSubmitService {
  /**
   * Bulk upload submit flow:
   * - Filter ready files and split PDFs vs non-PDFs.
   * - PDFs go through `BoardPdfFactoryService` using the chosen strategy/arrangement.
   * - Non-PDFs are queued into `sharedDataService.uploadItem` for standard upload insertion.
   * - Board files are handled via `BoardCloneService` and bypass the upload queue.
   */
  constructor(
    private readonly spaceRepo: SpaceRepository,
    private readonly spaceBoardsService: SpaceBoardsService,
    private readonly sharedDataService: SessionSharedDataService,
    private readonly boardCloneService: BoardCloneService,
    private readonly boardPdfFactoryService: BoardPdfFactoryService,
  ) {}

  async submitSingleFile(file: File) {
    if (this.boardCloneService.isBoardFile(file)) {
      await this.boardCloneService.importSpaceOrBoardFromFile(file);
      return;
    }

    this.queueFilesForUpload([file], true);
  }

  async submitImmediateFiles(files: File[], position?: CenterPosition) {
    const uploadFiles: File[] = [];

    for (const file of files) {
      if (this.boardCloneService.isBoardFile(file)) {
        await this.boardCloneService.importSpaceOrBoardFromFile(file);
        continue;
      }
      uploadFiles.push(file);
    }

    if (!uploadFiles.length) {
      return;
    }

    this.queueFilesForUpload(uploadFiles, true, position);
  }

  submit(options: BulkUploadSubmitOptions) {
    // Guard against invalid/empty state.
    if (!this.spaceRepo.activeSpace) {
      return;
    }

    const filesToUpload = options.files.filter(
      (file) => file.status === BulkUploadFileStatus.Ready,
    );
    if (!filesToUpload.length) {
      return;
    }

    const pdfFiles = filesToUpload.filter((file) => file.isPdf);
    const nonPdfFiles = filesToUpload.filter((file) => !file.isPdf);
    const arrangement = options.arrangement ?? PdfArrangement.Columns;
    const uploadAsSeparateImages = options.uploadAsSeparateImages ?? false;

    if (pdfFiles.length) {
      // Insert PDFs using the PDF factory (selection-aware).
      this.submitPdfFiles(pdfFiles, {
        strategy: options.strategy,
        arrangement,
        uploadAsSeparateImages,
        renderInNearestFreeSpace: options.renderInNearestFreeSpace,
      });
    }

    if (nonPdfFiles.length) {
      // Insert non-PDFs via the standard upload pipeline.
      this.submitNonPdfFiles(nonPdfFiles, options.renderInNearestFreeSpace);
    }
  }

  private submitPdfFiles(
    files: BulkUploadFile[],
    options: {
      strategy: PDFBoardStrategy;
      arrangement: PdfArrangement;
      uploadAsSeparateImages: boolean;
      renderInNearestFreeSpace?: boolean;
    },
  ) {
    // Convert selected pages to indices for PDF insertion.
    const fileEntries = this.buildPdfEntries(files);
    this.boardPdfFactoryService.bulkFlowInsertPdfItems({
      items: fileEntries,
      strategy: options.strategy,
      arrangement: options.arrangement,
      renderInNearestFreeSpace: options.renderInNearestFreeSpace,
      uploadAsSeparateImages: options.uploadAsSeparateImages,
    });
  }

  private submitNonPdfFiles(files: BulkUploadFile[], renderInNearestFreeSpace = true) {
    const uploadFiles = files.map((file) => file.file);
    this.queueFilesForUpload(uploadFiles, renderInNearestFreeSpace);
  }

  private buildPdfEntries(files: BulkUploadFile[]) {
    // Skip files with zero selected pages, default to all pages if no selection is made.
    return files
      .map((file) => {
        if (file.selectedPageIndices && file.selectedPageIndices.length === 0) {
          return undefined;
        }
        const selectedIndices =
          file.selectedPageIndices && file.selectedPageIndices.length > 0
            ? file.selectedPageIndices
            : file.pages.map((page) => page - 1);
        return {
          contentId: file.contentId,
          pdfName: file.name,
          selectedIndices,
        };
      })
      .filter((entry): entry is { contentId: string; pdfName: string; selectedIndices: number[] } =>
        Boolean(entry),
      );
  }

  private resolveActiveFrameUid(): string | undefined {
    return this.spaceBoardsService.activeSpaceSelectedBoard?.uid;
  }

  private queueFilesForUpload(
    files: File[],
    renderInNearestFreeSpace = true,
    position?: CenterPosition,
  ) {
    // Queue all files for insertion into the active board.
    const frameUid = this.resolveActiveFrameUid();
    if (!frameUid) {
      return;
    }

    const sessionId = this.spaceRepo.activeSpace?._id ?? '';
    const uploadFiles = files.map((file) =>
      this.createUploadFile(file, sessionId, frameUid, renderInNearestFreeSpace, position),
    );
    this.sharedDataService.uploadItem.next(uploadFiles);
  }

  private createUploadFile(
    file: File,
    sessionId: string,
    frameUid: string,
    renderInNearestFreeSpace: boolean,
    position?: CenterPosition,
  ) {
    // Keep UploadFile creation centralized to reduce duplication.
    const type = this.resolveFileType(file);
    return new UploadFile(file, file.name, file.size, type, sessionId, frameUid, {
      ...position,
      renderInNearestFreeSpace: position ? false : renderInNearestFreeSpace,
    });
  }

  private resolveFileType(file: File): ResourceItemModel {
    // Minimal copy of UploadFileService.extractFileType to avoid that dependency.
    if (isImageFile(file)) {
      return ResourceItemModel.IMAGE;
    }
    if (file.type.startsWith('video/')) {
      return ResourceItemModel.VIDEO;
    }
    if (file.type.startsWith('application/pdf') || this.isLibreOfficeFile(file)) {
      return ResourceItemModel.DOCUMENT;
    }
    return ResourceItemModel.ANY;
  }

  private isLibreOfficeFile(file: File): boolean {
    return (
      ACCEPTED_LIBREOFFICE_MIME_TYPES.some((mime) => file.type.startsWith(mime)) ||
      ACCEPTED_LIBREOFFICE_EXTENSIONS.some((ext) => file.name.endsWith(ext))
    );
  }
}
